import { FieldProps, useField } from 'formik';
import { FC, useCallback, useMemo, useState, useRef, ReactNode } from 'react';
import { ColorPicker, useColor } from 'react-color-palette';
import debounce from 'lodash.debounce';
import styled from 'styled-components';
import { Popover, TextField } from '@mui/material';
import { FormRowsDivider } from './components';

type ColorPickerFieldProps = FieldProps & {
  label?: ReactNode;

  /**
   * @default true
   */
  showPopularColors?: boolean;

  alpha?: boolean;
};

const popularColors = [
  '#262738',
  '#1E2761',
  '#460ee3',
  '#5f31e0',
  '#2F3C7E',
  '#00539C',
  '#234E70',
  '#4831D4',
  '#990011',
  '#7A2048',
  '#783937',
  '#8A307F',
  '#CC313D',
  '#2C5F2D',
  '#3A6B35',
  '#317773',
  '#B85042',
  '#EE4E34',
  '#408EC6',
  '#2BAE66',
  '#EC449B',
  '#6883BC',
  '#F96167',
  '#EA738D',
  '#FC766A',
  '#79A7D3',
  '#FF69B4',
  '#AA96DA',
  '#8AAAE5',
  '#89ABE3',
  '#97BC62',
  '#E0A96D',
  '#A7BEAE',
  '#FFA351',
  '#EEA47F',
  '#E3B448',
  '#F1AC88',
  '#DDC3A5',
  '#CBD18F',
  '#FFBE7B',
  '#ADD8E6',
  '#EED971',
  '#F7C5CC',
  '#E2D1F9',
  '#CCF381',
];

export const ColorPickerField: FC<ColorPickerFieldProps> = ({
  field,
  form,
  showPopularColors = true,
  label,
  alpha = false,
}) => {
  const [color, setColor] = useColor('hex', field.value || '');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [, meta] = useField(field.name);

  const handleOpen = () => {
    setPickerOpen(true);
  };

  const handleClose = () => {
    setPickerOpen(false);
  };

  const updateFormValue = useCallback(
    (value: string) => {
      form.setFieldValue(field.name, value);
    },
    [field.name, form]
  );

  const updateFormValueDebaunced = useMemo(() => debounce(updateFormValue), [updateFormValue]);

  return (
    <ColorPickerFieldWrapper ref={wrapperRef}>
      <TextField
        {...field}
        label={label}
        size="small"
        error={Boolean(meta.error)}
        helperText={meta.error}
        onBlur={(event) => {
          field.onBlur(event);
          form.submitForm();
        }}
        className="color-field"
      />

      <ColorPreview bgColor={field.value} onClick={handleOpen} />

      <Popover
        open={pickerOpen}
        anchorEl={wrapperRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverContent>
          <ColorPicker
            width={400}
            color={color}
            onChange={(color) => {
              setColor(color);
              updateFormValueDebaunced(color.hex);
            }}
            onChangeComplete={form.submitForm}
            hideHEX
            hideHSV
            hideRGB
            alpha={alpha}
          />

          {showPopularColors && (
            <>
              <FormRowsDivider />

              <PopularColors>
                {popularColors.map((color) => (
                  <ColorPreview
                    bgColor={color}
                    onClick={() => {
                      updateFormValue(color);
                      form.submitForm();
                    }}
                    size={37}
                  />
                ))}
              </PopularColors>
            </>
          )}
        </PopoverContent>
      </Popover>
    </ColorPickerFieldWrapper>
  );
};

const ColorPickerFieldWrapper = styled.div`
  display: flex;

  .color-field {
    margin-right: 12px;
    min-width: 240px;
  }
`;

const ColorPreview = styled.div<{ bgColor: string; size?: number }>`
  width: ${(p) => `${p.size || 40}px`};
  height: ${(p) => `${p.size || 40}px`};
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.25);
  background-color: ${(p) => p.bgColor};
`;

const PopularColors = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PopoverContent = styled.div`
  padding: 8px;
  max-width: 416px;
`;
